import React, { useEffect, useState, useRef } from 'react';
import { Input, Spin, Tag, Button } from 'antd';

import { useNavigate } from 'react-router-dom';
import httpInjectorService from '../../../../../services/http-injector.service';
import {
  SearchOutlined,
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import TemplateCard from './TemplateCard';
import TemplateRenderer from './TemplateRenderer';
import './templates.css';

export default function TemplateList() {
  const [allTemplates, setAllTemplates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [spaceDropdownOpen, setSpaceDropdownOpen] = useState(false);

  const [selectedType, setSelectedType] = useState('All');

  const navigate = useNavigate();

  const templateTypes = [
    'All',
    'popular',
    'meeting',
    'software',
    'design',
    'documentation',
    'teamwork',
  ];

  // FETCH TEMPLATES
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await httpInjectorService.getTemplate({ type: 'all' });
      if (response.status === 'success') {
        setAllTemplates(response.data);
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // FETCH SPACES
  const getSpaces = async () => {
    try {
      const response = await httpInjectorService.geOwnerSpaces();
      if (response.status === 'success') {
        setSpaces(response.data);
        setSelectedSpace(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching spaces:', error);
    }
  };

  useEffect(() => {
    fetchTemplates();
    getSpaces();
  }, []);

  // SEARCH & FILTER
  const handleSearch = (value) => {
    const filtered = allTemplates.filter((t) =>
      t.template_name.toLowerCase().includes(value.toLowerCase()),
    );
    setTemplates(filtered);
    setSelectedIndex(null);
  };

  const handleTypeChange = (categories) => {
    setSelectedType(categories);
    if (categories === 'All') {
      setTemplates(allTemplates);
    } else {
      setTemplates(allTemplates.filter((t) => t.categories === categories));
    }
    setSelectedIndex(null);
  };

  const selectedTemplate =
    selectedIndex !== null ? templates[selectedIndex] : null;

  const handleNext = () =>
    selectedIndex < templates.length - 1 && setSelectedIndex(selectedIndex + 1);

  const handleBack = () =>
    selectedIndex > 0 && setSelectedIndex(selectedIndex - 1);

  const handleSelectSpace = (space) => {
    setSelectedSpace(space);
    setSpaceDropdownOpen(false);
  };

  const handleUseTemplate = async () => {
    if (!selectedTemplate || !selectedSpace) return;

    const response = await httpInjectorService.templateDraftPage({
      space_id: selectedSpace.space_id,
      template_id: selectedTemplate.id,
    });

    if (response.status === 'success' && response.data?.item_id) {
      // Navigate to the newly created draft page
      navigate(
        `/admin/confluence/space/${selectedSpace.space_id}/page/${response.data.item_id}`,
      );
    } else {
      console.error('Failed to create template draft:', response);
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 100px)', display: 'flex' }}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          margin: 0,
          padding: 0,
        }}
      >
        {/* TEMPLATE LIST */}
        <div
          style={{
            flex: 1,
            padding: 0,
            overflowY: 'auto',
            minHeight: 20,
          }}
        >
          {/* Space selector */}
          <div style={{ position: 'relative', width: '100%' }}>
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
              }}
            >
              <b style={{ fontSize: '2rem' }}>Templates</b>

              {selectedSpace && (
                <div
                  onClick={() => setSpaceDropdownOpen(!spaceDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 6,
                    background: '#f5f5f5',
                  }}
                >
                  <img
                    src={selectedSpace.space_icon_url}
                    alt=""
                    style={{ width: 18, height: 18, borderRadius: 4 }}
                  />
                  <span
                    style={{
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedSpace.space_name}
                  </span>
                </div>
              )}
            </div>

            {/* Small Dropdown */}
            {spaceDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '60px',
                  left: 0,
                  width: '250px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  maxHeight: 250,
                  overflowY: 'auto',
                }}
              >
                {spaces.map((space) => (
                  <div
                    key={space.id}
                    onClick={() => handleSelectSpace(space)}
                    style={{
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      borderBottom: '1px solid #f5f5f5',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#f5f5f5')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'transparent')
                    }
                  >
                    <img
                      src={space.space_icon_url}
                      alt=""
                      style={{ width: 20, height: 20, borderRadius: 4 }}
                    />
                    <span
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {space.space_name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search + Type tags */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              // marginBottom: 24,
            }}
          >
            <Input
              placeholder="Search templates"
              prefix={<SearchOutlined />}
              style={{ width: 260 }}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {templateTypes.map((type) => (
                <Tag
                  key={type}
                  color={selectedType === type ? 'blue' : 'default'}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleTypeChange(type)}
                >
                  {type}
                </Tag>
              ))}
            </div>
          </div>

          <p>
            Displaying <strong>{templates.length}</strong> templates
          </p>

          {/* Template cards */}
          {loading ? (
            <Spin />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 24,
                marginTop: 20,
              }}
            >
              {templates.map((template, index) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isHovered={hoveredId === template.id}
                  isSelected={selectedIndex === index}
                  onHover={setHoveredId}
                  onLeave={() => setHoveredId(null)}
                  onSelect={() => setSelectedIndex(index)}
                  selectedSpace={selectedSpace}
                />
              ))}
            </div>
          )}
        </div>

        {/* TEMPLATE DETAILS */}
        {selectedTemplate && (
          <div
            style={{
              width: 380,
              padding: 24,
              background: '#fafafa',
              borderLeft: '1px solid #f0f0f0',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              height: '100%',
            }}
          >
            <div style={{ display: 'flex', gap: 12 }}>
              <LeftOutlined onClick={handleBack} />
              <RightOutlined onClick={handleNext} />
              <CloseOutlined
                style={{ marginLeft: 'auto' }}
                onClick={() => setSelectedIndex(null)}
              />
            </div>

            <div style={{ marginTop: 20 }}>
              <h2 style={{ fontSize: '1rem' }}>
                {selectedTemplate.template_name}
              </h2>
              <p style={{ fontSize: '0.7rem' }}>
                {selectedTemplate.tempdescription}
              </p>
              <Button
                style={{
                  backgroundColor: '#884b9e',
                  borderColor: '#884b9e',
                  color: '#fff',
                }}
                onClick={handleUseTemplate}
              >
                Use template
              </Button>
              <div
                style={{
                  backgroundColor: 'white',
                  marginTop: 4,
                  userSelect: 'none',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.cursor = 'not-allowed';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.cursor = 'default';
                }}
                onCopy={(e) => e.preventDefault()}
              >
                <TemplateRenderer content={selectedTemplate.content} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
